import { useContentfulContentTypeEntries } from '../useContentfulContentTypeEntries';

/**
 * Returns single Contentful entry for the provided content type.
 *
 * Additionally, returns `fetching` and `error` states.
 *
 * @example
 *
 * const { data, fetching, error } = useContentfulContentTypeEntry({
 *   contentType: 'contentfulContentTypeID',
 * });
 * const { fields, tags } = data;
 * const { image, bodyText, headingText } = fields ?? {};
 *
 * @see {@link https://www.contentful.com/developers/docs/javascript/tutorials/using-js-cda-sdk/#retrieve-all-entries-of-a-space Contentful Docs}
 */
export const useContentfulContentTypeEntry = <ContentTypeFields>(
  query: Omit<ContentfulQuery, 'limit'>,
) => {
  const { data, error, fetching } =
    useContentfulContentTypeEntries<ContentTypeFields>({ ...query, limit: 1 });

  return { data: data?.[0], error, fetching };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ContentfulQuery = {
  readonly contentType: string;
  readonly include?: number;
  readonly limit?: number;
  readonly order?: 'created-at' | 'updated-at';
  readonly pause?: boolean;
  readonly tags?: readonly string[];
};
